// components/forms/ContactForm.tsx
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { InputField } from "@/design-system/Atoms/InputField";
import { Button, Typography } from "@/design-system/Atoms";
import {
  loginFormSchemas,
  type LoginFormSchema,
} from "@/validation-schemas/login-form-schemas";
import { useLoginFormSubmission } from "@/rtkQuery/features/hooks/useLoginFormSubmission";
import { useNavigate } from "react-router-dom";
import { buildDashboardTabUrl } from "@/hooks/useBuildWithParams";
import { DASHBOARD_TABS } from "@/constants/dashboardTabs";
import { ROUTES } from "@/routes/routes.config";
import { Link } from "react-router-dom";
import { usePatientInformationService } from "@/rtkQuery/features/hooks/usePatientInformationService";
import { useState } from "react";
import { ForgetPasswordModal } from "@/design-system/Molecules/ForgetPasswordModal";

export const LoginFormSection = () => {
  const navigate = useNavigate();
  const { submitLoginForm, isLoading } = useLoginFormSubmission();
  const { refetchPatientEntitlements } = usePatientInformationService();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<LoginFormSchema>({
    resolver: zodResolver(loginFormSchemas),
  });

  const onSubmit = async (data: LoginFormSchema) => {
    try {
      const result = await submitLoginForm(data);
      if (result?.success) {
        const refetchResult = await refetchPatientEntitlements();
        const entitlements = refetchResult?.data;
        if (entitlements?.PatientInformationValid) {
          navigate(buildDashboardTabUrl(DASHBOARD_TABS.NOTIFICATION));
        } else {
          navigate(buildDashboardTabUrl(DASHBOARD_TABS.UPDATE));
        }
        reset();
      }
    } catch (err) {
      console.error("Login failed", err);
    }
  };
  const [isForgetPasswordModalOpen, setIsForgetPasswordModalOpen] =
    useState(false);
  const handleOpenModal = () => {
    setIsForgetPasswordModalOpen(true);
  };

  const closeForgetPasswordModal = () => {
    setIsForgetPasswordModalOpen(false);
  };
  return (
    <section id="login-feature" className="contact-form">
      <div className="flex flex-wrap">
        <div className="w-full xl:w-1/2 px-2">
          <div className="my-[4.68rem] lg:my-40">
            <div className="container mx-auto px-4">
              <div className="text-center mb-10">
                <Typography variant="sectionHeading" as="h2" className="mb-5">
                  Login
                </Typography>
                <Typography
                  variant="sectionDescriptionExtraSmall"
                  as="p"
                  className="mb-4"
                >
                  Log in to order medication and view your account details.
                </Typography>
              </div>
              <div className="bg-[#f6f7f9] p-6 rounded-[20px] lg:w-8/12 mx-auto">
                <form onSubmit={handleSubmit(onSubmit)} noValidate>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <InputField
                      placeholder="Email"
                      type="email"
                      register={register("email")}
                      error={errors.email?.message}
                      className="md:col-span-2"
                    />
                    <InputField
                      placeholder="Password"
                      type="password"
                      register={register("password")}
                      error={errors.password?.message}
                      className="md:col-span-2"
                    />
                  </div>
                  <div className="mb-6 justify-end text-right">
                    <Typography
                      variant="sectionDescriptionExtraSmall"
                      as="p"
                      className="leading-1.5"
                    >
                      <Link
                        to=""
                        rel="noopener noreferrer"
                        className="underline leading-[1.5]"
                        onClick={handleOpenModal}
                      >
                        Forgot password?
                      </Link>
                    </Typography>
                  </div>
                  <Button
                    variant="secondary"
                    size="xl"
                    className="!w-full"
                    type="submit"
                    disabled={isSubmitting || isLoading}
                    loading={isSubmitting || isLoading}
                  >
                    {isLoading ? "Submitting..." : "Login"}
                  </Button>
                </form>
              </div>
              <Typography
                variant="sectionDescriptionExtraSmall"
                as="p"
                className="mt-8 leading-1.5 text-center"
              >
                Don't have an account?{" "}
                <Link
                  to={ROUTES.QUESTIONNAIRES}
                  rel="noopener noreferrer"
                  className="underline leading-[1.5]"
                >
                  Get Started
                </Link>
              </Typography>
            </div>
          </div>
        </div>
        <div className="w-full xl:w-1/2 p-0">
          <img
            src="/images/login/img-signup.png"
            alt=""
            className="w-full h-full object-cover align-middle"
          />
        </div>
      </div>
      <ForgetPasswordModal
        isOpen={isForgetPasswordModalOpen}
        onClose={closeForgetPasswordModal}
      />
    </section>
  );
};
